<template>
  <div class="board-picker" data-test="board-picker">
    <p v-if="response.boards.length === 0" class="picker-empty" data-test="picker-empty">
      No boards found for <code>{{ response.project }}</code> — check the key,
      or paste a board URL directly.
    </p>

    <template v-else>
      <p class="picker-hint">
        <code>{{ response.project }}</code> has {{ response.boards.length }}
        board{{ response.boards.length === 1 ? "" : "s" }}. Uncheck anything
        stale — each checked board gets a full backfill and a nightly cron.
      </p>

      <ul class="picker-list">
        <li
          v-for="b in response.boards"
          :key="b.board_id"
          class="picker-row"
          data-test="picker-row"
        >
          <label class="picker-main">
            <input
              type="checkbox"
              :checked="selected.has(b.board_id)"
              :disabled="busy"
              data-test="picker-check"
              @change="toggle(b.board_id, ($event.target as HTMLInputElement).checked)"
            />
            <span class="picker-name">{{ b.name }}</span>
            <span :class="['type-badge', b.type === 'scrum' ? 'scrum' : 'kanban']">
              {{ b.type === "simple" ? "kanban" : b.type }}
            </span>
            <span
              v-if="b.already_onboarded"
              class="onboarded-badge"
              data-test="picker-onboarded-badge"
            >already onboarded</span>
            <span
              v-if="b.location_project && b.location_project !== response.project"
              class="notice-badge"
              data-test="picker-location-badge"
              :title="`This board lives in project ${b.location_project} — it appears here because its filter includes ${response.project} issues.`"
            >in {{ b.location_project }}</span>
            <span
              v-if="sharedFilterPeers.has(b.board_id)"
              class="notice-badge warn"
              data-test="picker-shared-filter-badge"
              :title="`Same saved filter as ${peerNames(b.board_id)} — onboarding both scores the same sprints twice.`"
            >same filter as {{ peerNames(b.board_id) }}</span>
          </label>
          <span class="picker-stats">
            <span class="stat" :title="`Issues touched in the last 30 days`">
              {{ b.recent_tickets ?? "—" }} tickets/30d
            </span>
            <span class="stat">
              {{ b.contributors ?? "—" }}{{ b.contributors_lower_bound ? "+" : "" }} contributors
            </span>
            <span class="stat">
              {{ b.last_activity ? `active ${b.last_activity}` : "no recent activity" }}
            </span>
          </span>
        </li>
      </ul>

      <p v-if="response.stats_truncated" class="picker-truncated" data-test="picker-truncated">
        Stats were computed for the first 15 boards only.
      </p>

      <button
        type="button"
        class="onboard-btn"
        :disabled="selected.size === 0 || busy"
        data-test="picker-confirm"
        @click="confirm"
      >
        Onboard {{ selected.size }} board{{ selected.size === 1 ? "" : "s" }}
      </button>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import type { DiscoveredBoard, DiscoverBoardsResponse } from "@/api/admin";

const props = defineProps<{
  response: DiscoverBoardsResponse;
  busy: boolean;
}>();

const emit = defineEmits<{
  confirm: [boards: DiscoveredBoard[]];
}>();

/** Boards grouped by saved filter; only groups of 2+ are duplicate views. */
function sharedFilterGroups(boards: DiscoveredBoard[]): DiscoveredBoard[][] {
  const byFilter = new Map<number, DiscoveredBoard[]>();
  for (const b of boards) {
    if (b.filter_id == null) continue;
    byFilter.set(b.filter_id, [...(byFilter.get(b.filter_id) ?? []), b]);
  }
  return [...byFilter.values()].filter((g) => g.length > 1);
}

const sharedFilterPeers = computed<Map<number, DiscoveredBoard[]>>(() => {
  const peers = new Map<number, DiscoveredBoard[]>();
  for (const group of sharedFilterGroups(props.response.boards)) {
    for (const b of group) {
      peers.set(b.board_id, group.filter((o) => o.board_id !== b.board_id));
    }
  }
  return peers;
});

function peerNames(boardId: number): string {
  return (sharedFilterPeers.value.get(boardId) ?? []).map((p) => `“${p.name}”`).join(", ");
}

// Preselect everything that is not known-onboarded (null = unknown → checked;
// re-running an onboarded board is a fast no-op server-side anyway).
const selected = ref<Set<number>>(new Set());

watch(
  () => props.response,
  (r) => {
    const next = new Set(
      r.boards.filter((b) => b.already_onboarded !== true).map((b) => b.board_id),
    );
    // Among boards sharing one saved filter, keep at most one checked —
    // they show the same issues, so onboarding several double-scores the
    // sprints. Prefer the board located in the requested project; if a group
    // member is already onboarded, the data is flowing and none stay checked.
    for (const group of sharedFilterGroups(r.boards)) {
      const keep = group.some((b) => b.already_onboarded === true)
        ? null
        : (group.find((b) => b.location_project === r.project) ?? group[0]);
      for (const b of group) {
        if (b !== keep) next.delete(b.board_id);
      }
    }
    selected.value = next;
  },
  { immediate: true },
);

function toggle(boardId: number, checked: boolean): void {
  const next = new Set(selected.value);
  if (checked) next.add(boardId);
  else next.delete(boardId);
  selected.value = next;
}

function confirm(): void {
  emit("confirm", props.response.boards.filter((b) => selected.value.has(b.board_id)));
}
</script>

<style scoped>
.board-picker {
  display: grid;
  gap: 14px;
}

.picker-hint,
.picker-empty,
.picker-truncated {
  font-size: 13px;
  color: var(--muted-2);
  margin: 0;
}
.picker-hint code,
.picker-empty code {
  font-family: "JetBrains Mono", monospace;
  font-size: 12px;
  color: var(--ink-1);
}

.picker-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 8px;
}

.picker-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 12px 14px;
  background: var(--bg);
  border: 1px solid var(--line-2);
  border-radius: 10px;
}

.picker-main {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.picker-main input { cursor: pointer; }

.picker-name {
  font-size: 14px;
  color: var(--ink-1);
}

.type-badge {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-tint);
  color: var(--ink-2);
}
.type-badge.scrum  { background: var(--brand-soft); color: var(--brand); }
.type-badge.kanban { background: var(--bg-tint); color: var(--ink-2); }

.onboarded-badge,
.notice-badge {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--bg-tint);
  color: var(--muted-2);
}
.notice-badge { cursor: help; }
.notice-badge.warn {
  background: var(--amber-soft);
  color: var(--amber-ink);
}

.picker-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-family: "JetBrains Mono", monospace;
  font-size: 11px;
  color: var(--muted-2);
}

.onboard-btn {
  justify-self: start;
  background: var(--brand);
  color: white;
  border: 0;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: opacity var(--speed-quick) var(--ease);
}
.onboard-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
